import { useEffect, useEffectEvent, useState } from 'react';
import { StanModal } from '@/components';
import { useEditFlowForm } from '../hooks/useEditFlowForm';
import { EditFlowDetailsSection, EditFlowSummarySection } from './edit-flow';
import type { FlowSummary } from '@/lib/api/email-flows/types';

interface EditFlowModalProps {
    isOpen: boolean;
    flow: FlowSummary | null;
    onClose: () => void;
}

export const EditFlowModal = ({ isOpen, flow, onClose }: EditFlowModalProps) => {
    const [pastAllowedRescheduleTime] = useState(false);

    const { form, onSubmit, isDateTriggerActive, isCartAbandonedTriggerActive, selectedTrigger, isSubmitting } =
        useEditFlowForm({ flow, isOpen, onClose });

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        trigger,
        formState: { errors },
    } = form;

    const showRemoveRecipients = watch('showRemoveRecipients');
    const selectedDate = watch('selectedDate');
    const selectedTime = watch('selectedTime');
    const updatesDisabled = flow?.status === 'published' && pastAllowedRescheduleTime;
    const audienceTags = flow?.data?.specific_time?.tags || [];
    const recipientCount = flow?.recipient_count || 0;

    const handleValidation = useEffectEvent(() => {
        if (isDateTriggerActive && selectedDate) {
            trigger(['selectedDate', 'selectedTime']);
        }
    });

    // Re-validate when date or time changes to clear errors immediately
    useEffect(() => {
        handleValidation();
    }, [selectedDate, selectedTime, isDateTriggerActive]);

    return (
        <StanModal
            id='edit-flow-modal'
            isOpen={isOpen}
            onClose={onClose}
            title='Edit Flow'
            size='xl'
            primaryActionLabel='Confirm'
            secondaryActionLabel='Cancel'
            onPrimaryAction={handleSubmit(onSubmit)}
            onSecondaryAction={onClose}
            primaryActionDisabled={isSubmitting}
        >
            <form className='flex flex-col'>
                <EditFlowDetailsSection
                    control={control}
                    errors={errors}
                    isDateTriggerActive={isDateTriggerActive}
                    isCartAbandonedTriggerActive={isCartAbandonedTriggerActive}
                    showRemoveRecipients={showRemoveRecipients || false}
                    selectedDate={watch('selectedDate')}
                    selectedTime={watch('selectedTime') ?? 0}
                    onDateChange={date => setValue('selectedDate', date)}
                    onTimeChange={time => setValue('selectedTime', time)}
                    updatesDisabled={updatesDisabled}
                />

                <EditFlowSummarySection
                    selectedTrigger={selectedTrigger}
                    recipientCount={recipientCount}
                    audienceTags={audienceTags}
                    isDateTriggerActive={isDateTriggerActive}
                />
            </form>
        </StanModal>
    );
};
